import { Fragment } from 'react';
import type { ReactNode } from 'react';
import type { CellContext, ResolvedColumn, SortState } from '../../domain';
import { ExpandedRow, Row } from './Body';
import { DragCell, leadingCell, MenuCell, SelectHeader } from './Cells';
import { Loading, SkeletonRows } from './Empty';
import { Head } from './Head';
import { useCellOverflowTooltip } from './OverflowTooltip';
import { useColumnResize } from './use-column-resize';
import { useHeaderWheelScroll } from './use-header-wheel-scroll';
import { useRowDrag } from './use-row-drag';

export interface SelectConfig<T> {
    mode: 'single' | 'multi';
    isSelected: (row: T) => boolean;
    canSelect: (row: T) => boolean;
    headerState: 'none' | 'some' | 'all';
    onToggle: (row: T) => void;
    onToggleAll: () => void;
}

export interface ExpandConfig<T> {
    header?: string;
    enabled: (row: T) => boolean;
    count?: (row: T) => number;
    isExpanded: (row: T) => boolean;
    isRendered: (row: T) => boolean;
    onCollapsed: (row: T) => void;
    onToggle: (row: T) => void;
    render: (row: T) => ReactNode;
}

export interface DragConfig<T> {
    enabled: (row: T) => boolean;
    onReorder: (from: number, to: number) => void;
}

export interface ViewProps<T> {
    rows: T[];
    columns: ResolvedColumn<T>[];
    getRowId: (row: T) => string;
    describedBy?: string;
    api: CellContext<T>['api'];

    sort: SortState | null;
    onSort: (col: ResolvedColumn<T>) => void;

    loading?: boolean;
    skeletonRowCount?: number;
    empty?: ReactNode;

    select?: SelectConfig<T>;
    expand?: ExpandConfig<T>;
    drag?: DragConfig<T>;

    rowMenu?: (row: T) => ReactNode;
    onRowClick?: (row: T) => void;
    onResize?: (columnId: string, width: number) => void;

    collapsibleGroups?: Set<string>;
    isGroupCollapsed?: (key: string) => boolean;
    onToggleGroup?: (key: string) => void;
}

export function TableView<T>(props: ViewProps<T>): JSX.Element {
    const {
        rows,
        getRowId,
        describedBy,
        api,
        sort,
        onSort,
        loading,
        skeletonRowCount,
        empty,
    } = props;
    const { select, expand, drag, rowMenu, onRowClick, onResize } = props;
    const { collapsibleGroups, isGroupCollapsed, onToggleGroup } = props;

    const rowDrag = useRowDrag(drag?.onReorder);
    const resize = useColumnResize<T>(onResize);
    const columns = resize.apply(props.columns);

    const leading: JSX.Element[] = [];
    if (select) {
        leading.push(
            <th key="__select" className="ics-table__cell--select">
                {select.mode === 'multi' ? (
                    <SelectHeader
                        state={select.headerState}
                        onToggle={select.onToggleAll}
                    />
                ) : null}
            </th>,
        );
    }
    if (expand) {
        leading.push(
            <th key="__expand" className="ics-table__cell--expand">
                {expand.header ?? ''}
            </th>,
        );
    }
    const trailing: JSX.Element[] = rowMenu
        ? [
              <th
                  key="__menu"
                  className="ics-table__head-cell ics-table__cell--menu"
              />,
          ]
        : [];
    const colSpan = columns.length + leading.length + trailing.length;
    const hasRows = rows.length > 0;

    const tableClassName = [
        'ics-table',
        select ? null : 'ics-table--no-select',
        resize.resizingId ? 'ics-table--resizing' : null,
    ]
        .filter(Boolean)
        .join(' ');

    const wrapperRef = useHeaderWheelScroll<HTMLDivElement>();

    const overflowTooltip = useCellOverflowTooltip();

    function renderDragHandle(row: T, id: string, rowIndex: number): ReactNode {
        if (!drag?.enabled(row)) {
            return <span className="ics-table__row-drag-spacer" />;
        }
        return (
            <DragCell
                label={`Reorder row ${id}`}
                onDragStart={() => {
                    rowDrag.start(rowIndex);
                }}
                onDragEnd={rowDrag.end}
                onMove={(dir) => {
                    rowDrag.move(rowIndex, dir);
                }}
            />
        );
    }

    function renderBody(): ReactNode {
        if (loading && !hasRows) {
            return (
                <SkeletonRows
                    columns={columns}
                    select={select ? { mode: select.mode } : undefined}
                    expand={
                        expand
                            ? { showCount: expand.count !== undefined }
                            : undefined
                    }
                    menu={rowMenu !== undefined}
                    rowCount={skeletonRowCount}
                />
            );
        }

        if (!hasRows) {
            return null;
        }

        return rows.map((row, rowIndex) => {
            const id = getRowId(row);
            const expanded = expand ? expand.isExpanded(row) : false;
            const trailingCell = rowMenu ? (
                <td className="ics-table__cell ics-table__cell--menu">
                    <MenuCell>{rowMenu(row)}</MenuCell>
                </td>
            ) : null;
            const dragHandle = drag
                ? renderDragHandle(row, id, rowIndex)
                : null;

            return (
                <Fragment key={id}>
                    <Row
                        row={row}
                        rowIndex={rowIndex}
                        columns={columns}
                        api={api}
                        selected={select?.isSelected(row)}
                        onClick={onRowClick}
                        leading={leadingCell(row, id, select, expand)}
                        trailing={trailingCell}
                        dragHandle={dragHandle}
                        drop={
                            drag
                                ? {
                                      dragging: rowDrag.isDragging(rowIndex),
                                      dropEdge: rowDrag.dropEdge(rowIndex),
                                      onDragOver: () => {
                                          rowDrag.over(rowIndex);
                                      },
                                      onDrop: () => {
                                          rowDrag.drop(rowIndex);
                                      },
                                  }
                                : undefined
                        }
                    />
                    {expand?.isRendered(row) ? (
                        <ExpandedRow
                            colSpan={colSpan}
                            expanded={expanded}
                            onCollapsed={() => {
                                expand.onCollapsed(row);
                            }}
                        >
                            {expand.render(row)}
                        </ExpandedRow>
                    ) : null}
                </Fragment>
            );
        });
    }

    return (
        <div
            className="ics-table__wrapper"
            ref={wrapperRef}
            {...overflowTooltip.handlers}
        >
            <table
                role="grid"
                aria-describedby={describedBy}
                className={tableClassName}
            >
                <Head
                    columns={columns}
                    sort={sort}
                    onSort={onSort}
                    onResizeStart={onResize ? resize.start : undefined}
                    leading={leading}
                    trailing={trailing}
                    collapsibleGroups={collapsibleGroups}
                    isGroupCollapsed={isGroupCollapsed}
                    onToggleGroup={onToggleGroup}
                />
                <tbody>{renderBody()}</tbody>
            </table>
            {overflowTooltip.tooltip}
            {!hasRows && !loading ? (
                <div className="ics-table__empty-cell">{empty}</div>
            ) : null}
            {loading && hasRows ? <Loading /> : null}
        </div>
    );
}
